import { CART_PAGE } from 'constants/amethystPageTypes';
import { HYDRA_RET } from 'constants/hydraTests';
import { evAddToFavorites } from 'events/favorites';
import { toFloatInt } from 'helpers/NumberFormats';
import { getAmethystPageType, trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { isAssigned } from 'actions/ab';
import { middlewareTrack, titaniteView, track } from 'apis/amethyst';
import { sendMonetateEvent } from 'apis/monetate';

const CART_PAGEVIEW = 'CART_PAGEVIEW';
const EVENT_CART_QUANTITY = 'EVENT_CART_QUANTITY';
const EVENT_NAVIGATE_TO_CHECKOUT = 'EVENT_NAVIGATE_TO_CHECKOUT';
const EVENT_MOVE_TO_FAVORITES = 'EVENT_MOVE_TO_FAVORITES';
const EVENT_ADD_TO_CART = 'EVENT_ADD_TO_CART';
const EVENT_MODIFY_QUANTITY = 'EVENT_MODIFY_QUANTITY';
const EVENT_REMOVE_FROM_CART = 'EVENT_REMOVE_FROM_CART';

export const cartUpdatedEvent = () => ({
  type: EVENT_CART_QUANTITY
});

export const navigateToCheckoutEvent = () => ({
  type: EVENT_NAVIGATE_TO_CHECKOUT
});

export const moveToFavoritesEvent = asin => ({
  type: EVENT_MOVE_TO_FAVORITES,
  asin
});

export const addToCartEvent = product => ({
  type: EVENT_ADD_TO_CART,
  product
});

export const modifyQuantityEvent = (quantity, asin) => ({
  type: EVENT_MODIFY_QUANTITY,
  quantity,
  asin
});

export const removeFromCartEvent = asin => ({
  type: EVENT_REMOVE_FROM_CART,
  asin
});

export const pvCart = cart => {
  if (!cart.activeItems) {
    return;
  }

  const {
    isCartModal = false,
    activeItemTotalQuantity: itemsInCart,
    subtotal: { amount: grandTotal } = {},
    activeItems
  } = cart;

  titaniteView();

  const products = activeItems.map(({ asin, productId, styleId, stockId }) => ({
    asin,
    productId,
    styleId,
    stockId
  }));

  const cartPageView = {
    isCartModal,
    itemsInCart,
    grandTotal
  };

  // if there are products, include them into the payload
  if (products.length) {
    cartPageView.products = products;
  }

  return { cartPageView };
};

const addToCartOrBuyNow = ({
  productId,
  styleId,
  price,
  incompleteAddToCart = false,
  missingDimension,
  addedFrom
}) => {
  const formattedPrice = (typeof price === 'string') ? toFloatInt(price) : price;
  return {
    productIdentifiers: {
      productId,
      styleId
    },
    price: formattedPrice,
    incompleteAddToCart,
    missingDimension,
    addedFrom
  };
};

export const evAddToCart = bundle => ({ addToCart: addToCartOrBuyNow(bundle) });
export const evBuyNow = bundle => ({ buyNowClick: addToCartOrBuyNow(bundle) });

export const evRemoveFromCart = ({
  activeItems,
  savedItems,
  asin
}) => {
  const items = [ ...activeItems, ...savedItems ];
  const product = items.filter(item => item.asin === asin);
  const originalQuantity = product[0].quantity;

  return {
    removeFromCart: {
      originalQuantity,
      productIdentifiers: { asin }
    }
  };
};

export const evModifyQuantity = ({
  activeItems,
  quantity: newQuantity,
  asin
}) => {
  const product = activeItems.filter(item => item.asin === asin);
  const originalQuantity = product[0].quantity;

  return {
    modifyQuantity: {
      originalQuantity,
      newQuantity,
      productIdentifiers: { asin }
    }
  };
};

export const evProceedToCheckout = ({ pageType }) => ({
  proceedToCheckout: {
    pageType
  }
});

export const evSomeTest = ({
  foo: { bar: { baz } }
}) => ({
  someTest: {
    foo: baz
  }
});

const monetateCartView = appState => {
  const { cart: { cartObj } } = appState;
  const cartItems = cartObj.activeItems.map(v => {
    const { productId, price, styleId, quantity } = v;
    return { productId, sku: styleId, quantity, unitPrice: price };
  });
  sendMonetateEvent(
    ['setPageType', 'cart'],
    ['addCartRows', cartItems]
  );
};

const cartView = appState => {
  const { cart: { cartObj } } = appState;
  track(pvCart, cartObj);
  evOnShowRewardsTransparencyCart(appState);
};

const moveToFavorites = (_, { asin }) => {
  track(evAddToFavorites, {
    asin,
    addedFrom: CART_PAGE
  });

  trackEvent('TE_CART_ADDTOFAVORITES', asin);
};

const addToCart = (_, { product }) => {
  track(evAddToCart, { ...product, addedFrom: CART_PAGE });
};

const removeFromCart = (appState, { asin }) => {
  const { cart: { cartObj } } = appState;

  trackLegacyEvent('CartRemoveItem', null, `asin:${asin}`);
  trackEvent('TE_CART_REMOVEITEM', asin);
  track(evRemoveFromCart, { ...cartObj, asin });
};

const modifyQuantity = (appState, { quantity, asin }) => {
  const { cart: { cartObj } } = appState;

  trackEvent('TE_CART_MODIFYQUANTITY', asin);
  track(evModifyQuantity, { ...cartObj, quantity, asin });
};

export const evOnShowRewardsTransparencyCart = appState => {
  const { cookies: { 'x-main': xMain }, pageView: { pageType }, sharedRewards: { transparencyPointsForCart, isEnrolled } } = appState;
  const isInTransparencyTest = isAssigned(HYDRA_RET, 1, appState);

  if (isInTransparencyTest && transparencyPointsForCart) {

    const { totalPoints: { points, dollarValue } = {} } = transparencyPointsForCart;

    // ZFC event
    const recognizedStatus = xMain ? 'recognized' : 'unrecognized';
    const enrolledStatus = isEnrolled ? 'enrolled' : 'unenrolled';
    trackEvent('TE_SHOW_REWARDS_TRANSPARENCY', `${pageType}:${points}:${recognizedStatus}:${enrolledStatus}}`);

    // Amethyst event
    middlewareTrack({
      rewardsTransparencyImpression: {
        sourcePage: getAmethystPageType(pageType),
        isEnrolled: !!isEnrolled,
        rewardsPoints: points,
        rewardsDollars: dollarValue
      }
    });
  }
};

// Quote these properties because they are indirectly referenced by the saga middleware and we don't want them mangled.
export default {
  pageEvent: CART_PAGEVIEW,
  events: {
    [CART_PAGEVIEW]: [
      monetateCartView,
      cartView,
      () => trackEvent('TE_PV_CARTPAGE')
    ],
    [EVENT_CART_QUANTITY]: [monetateCartView],
    [EVENT_NAVIGATE_TO_CHECKOUT]: [
      () => track(evProceedToCheckout, { pageType: CART_PAGE }),
      () => trackEvent('TE_CART_PROCEEDTOCHECKOUT')
    ],
    [EVENT_MOVE_TO_FAVORITES]: [moveToFavorites],
    [EVENT_ADD_TO_CART]: [addToCart],
    [EVENT_MODIFY_QUANTITY]: [modifyQuantity],
    [EVENT_REMOVE_FROM_CART]: [removeFromCart]
  }
};
